import React from "react";
import { Globe } from "lucide-react";

/**
 * Small elegant icon on each grocery row that opens a menu of online
 * vendors (those with a `search_url_template`). Selecting one deep-links
 * to that vendor's search results for the item name. If only one online
 * vendor exists, clicking opens it directly.
 */
export default function OnlineShopIcon({ itemName, onlineVendors }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!onlineVendors || onlineVendors.length === 0) return null;

  const linkFor = (v) => v.search_url_template.replace("{q}", encodeURIComponent(itemName));

  // Single vendor — direct link, no menu.
  if (onlineVendors.length === 1) {
    const v = onlineVendors[0];
    return (
      <a
        href={linkFor(v)}
        target="_blank"
        rel="noreferrer"
        title={`Search ${v.name} for ${itemName}`}
        data-testid={`grocery-online-${v.id}`}
        className="p-1.5 rounded-full text-hearth-mute hover:text-hearth-clayDark hover:bg-hearth-clay/10 transition"
      >
        <Globe size={13} strokeWidth={1.7} />
      </a>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((x) => !x)}
        title="Search online"
        data-testid="grocery-online-toggle"
        className="p-1.5 rounded-full text-hearth-mute hover:text-hearth-clayDark hover:bg-hearth-clay/10 transition"
      >
        <Globe size={13} strokeWidth={1.7} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-20 min-w-[180px] rounded-2xl border border-hearth-line bg-hearth-paper shadow-lg py-1.5"
          data-testid="grocery-online-menu"
        >
          <div className="px-3 py-1 caption text-hearth-mute">Search for &ldquo;{itemName}&rdquo; on:</div>
          {onlineVendors.map((v) => (
            <a
              key={v.id}
              href={linkFor(v)}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              data-testid={`grocery-online-${v.id}`}
              className="block px-3 py-1.5 text-sm text-hearth-ink hover:bg-hearth-stone transition"
            >
              {v.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
